import React, {useMemo, useRef, useState} from 'react';
import {Accordion, Modal, Textarea} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  SkillCriterionEvidenceTypeEnum,
  SkillSignOffCriterionResultEnum,
  SkillSignOffResultEnum,
} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetCourseSessionQuery} from 'src/redux/queries/courseSession';
import {useGetSkillQuery} from 'src/redux/queries/skill';
import {
  useEvaluateSkillSignOffMutation,
  useGetSkillSignOffQuery,
} from 'src/redux/queries/skillSignOff';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useUnsavedChangesGuard} from 'src/shared/hooks/useUnsavedChangesGuard';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type CriterionResultState = Record<
  number,
  {result: SkillSignOffCriterionResultEnum; note: string}
>;

const RESULT_OPTIONS: {
  value: SkillSignOffCriterionResultEnum;
  label: string;
  color: string;
}[] = [
  {value: SkillSignOffCriterionResultEnum.Pass, label: 'Pass', color: 'green'},
  {value: SkillSignOffCriterionResultEnum.Fail, label: 'Fail', color: 'red'},
  {
    value: SkillSignOffCriterionResultEnum.NotApplicable,
    label: 'N/A',
    color: 'gray',
  },
];

/**
 * A Fail on any criterion blocks Sign off; use Not yet competent instead.
 * "Previous attempt" fields are server-resolved to the latest prior
 * Not-Yet-Competent attempt for the same skill+trainee.
 */
export const SignOffEvaluatePage: React.FC = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {signOffId} = useParams();
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [results, setResults] = useState<CriterionResultState>({});
  const [signOffComment, setSignOffComment] = useState('');
  const [notYetOpened, notYetHandlers] = useDisclosure(false);
  const [notYetComment, setNotYetComment] = useState('');
  // Once a submit-then-navigate is underway, the unsaved-changes guard must
  // stay disarmed for the rest of that flow. isSubmitting (from the mutation
  // hook) and the post-success navigate() land in different microtask turns,
  // so there's a render in between where isSubmitting has already flipped
  // back to false but the just-submitted `results`/comments haven't been
  // cleared yet — a state boolean derived from those would flicker "dirty"
  // for that one render and could arm the blocker for the navigate() that
  // follows. A ref sidesteps the flicker entirely: it's read synchronously
  // during render, so every render in that window (regardless of which
  // microtask produced it) sees the same disarmed value.
  const suppressGuardRef = useRef(false);

  const parsedId = signOffId ? parseInt(signOffId) : undefined;

  const {
    data: signOff,
    isLoading: isSignOffLoading,
    error,
  } = useGetSkillSignOffQuery(parsedId!, {skip: !parsedId});
  const {data: skill, isLoading: isSkillLoading} = useGetSkillQuery(
    signOff?.skillId ?? 0,
    {skip: !signOff?.skillId}
  );
  const {data: session} = useGetCourseSessionQuery(signOff?.sessionId ?? 0, {
    skip: !signOff?.sessionId,
  });
  const [evaluate, {isLoading: isSubmitting}] =
    useEvaluateSkillSignOffMutation();

  const criteria = useMemo(
    () => (skill?.criteria ?? []).slice().sort((a, b) => a.order - b.order),
    [skill]
  );

  const previousAttemptComment = signOff?.previousAttemptComment ?? undefined;
  const previousAttemptAt = signOff?.previousAttemptAt ?? undefined;
  const previousAttemptByName = signOff?.previousAttemptByName ?? undefined;
  const hasPreviousAttempt =
    previousAttemptComment !== undefined || previousAttemptAt !== undefined;

  const isLoading = isSignOffLoading || (Boolean(signOff) && isSkillLoading);
  const isPending =
    signOff !== undefined &&
    (signOff.result === undefined || signOff.result === null);

  const isDirty =
    isPending &&
    (Object.keys(results).length > 0 ||
      signOffComment.trim() !== '' ||
      notYetComment.trim() !== '');
  const {isBlocked, confirmLeave, cancelLeave} = useUnsavedChangesGuard(
    isDirty && !isSubmitting && !suppressGuardRef.current
  );

  const allMarked =
    criteria.length > 0 &&
    criteria.every(criterion => results[criterion.id] !== undefined);
  const hasFail = criteria.some(
    criterion =>
      results[criterion.id]?.result === SkillSignOffCriterionResultEnum.Fail
  );
  const hasPass = criteria.some(
    criterion =>
      results[criterion.id]?.result === SkillSignOffCriterionResultEnum.Pass
  );
  const isAllNotApplicable =
    allMarked &&
    criteria.every(
      criterion =>
        results[criterion.id]?.result ===
        SkillSignOffCriterionResultEnum.NotApplicable
    );
  const needsAllNaComment = isAllNotApplicable && signOffComment.trim() === '';
  const canSignOff =
    allMarked &&
    !hasFail &&
    (hasPass || (isAllNotApplicable && !needsAllNaComment));
  const hasAnyCriterionNote = criteria.some(
    criterion => (results[criterion.id]?.note ?? '').trim() !== ''
  );
  const needsNotYetComment =
    !hasAnyCriterionNote && notYetComment.trim() === '';
  const canSubmitNotYetCompetent = allMarked;

  const buildCriterionResults = () =>
    criteria.map(criterion => ({
      criterionId: criterion.id,
      result: results[criterion.id]!.result,
      note: results[criterion.id]!.note || undefined,
    }));

  const handleMark = (
    criterionId: number,
    result: SkillSignOffCriterionResultEnum
  ) => {
    setResults(current => ({
      ...current,
      [criterionId]: {result, note: current[criterionId]?.note ?? ''},
    }));
  };

  const handleNote = (criterionId: number, note: string) => {
    setResults(current => ({
      ...current,
      [criterionId]: {
        result:
          current[criterionId]?.result ?? SkillSignOffCriterionResultEnum.Pass,
        note,
      },
    }));
  };

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'sign-off-evaluate',
  });
  const {goBack: exit} = useBackNavigation(fallbackTo);

  const handleSignOff = async () => {
    if (!parsedId) {
      return;
    }
    suppressGuardRef.current = true;
    try {
      await evaluate({
        signOffId: parsedId,
        overallResult: SkillSignOffResultEnum.SignedOff,
        comment: signOffComment.trim() || undefined,
        criterionResults: buildCriterionResults(),
      }).unwrap();
      setAlert(
        `${signOff?.userName} was signed off on ${signOff?.skillName}.`,
        'success'
      );
      exit();
    } catch (submitError) {
      suppressGuardRef.current = false;
      setAlert(parseErrorMessage(submitError), 'error');
    }
  };

  const handleNotYetCompetent = async () => {
    if (!parsedId) {
      return;
    }
    suppressGuardRef.current = true;
    try {
      await evaluate({
        signOffId: parsedId,
        overallResult: SkillSignOffResultEnum.NotYetCompetent,
        comment: notYetComment.trim(),
        criterionResults: buildCriterionResults(),
      }).unwrap();
      notYetHandlers.close();
      setAlert(
        `${signOff?.userName} was marked Not Yet Competent. A new sign-off request was created.`,
        'success'
      );
      exit();
    } catch (submitError) {
      suppressGuardRef.current = false;
      setAlert(parseErrorMessage(submitError), 'error');
    }
  };

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <BackBar
        fallbackTo={fallbackTo}
        fallbackLabel={fallbackLabel}
        breadcrumbs={breadcrumbs}
      />

      {error ? (
        <Alert color="red" mt="md">
          {parseErrorMessage(error)}
        </Alert>
      ) : isLoading || !signOff ? (
        <Spinner loadingText="Loading sign-off..." />
      ) : !isPending ? (
        <Stack mt="md" gap="md">
          <Alert color="gray">This sign-off was already evaluated.</Alert>
          <Box>
            <Button
              onClick={() =>
                navigate(PathUtility.TrainingSignOffDetail(String(signOff.id)))
              }>
              View details
            </Button>
          </Box>
        </Stack>
      ) : (
        <>
          <Box mb="md">
            <Text fw={700} size="xl">
              {signOff.userName}
            </Text>
            <Text c="dimmed">{signOff.skillName}</Text>
            {skill?.description && (
              <Text size="sm" mt={4}>
                {skill.description}
              </Text>
            )}
            <Group gap="md" mt={4}>
              {session?.definition.name && (
                <Text size="xs" c="dimmed">
                  Course: {session.definition.name}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                Requested{' '}
                {parseUtcDate(signOff.requestedAt).toLocaleDateString()}
              </Text>
            </Group>
          </Box>

          {hasPreviousAttempt && (
            <Accordion variant="separated" mb="md">
              <Accordion.Item value="previous-attempt">
                <Accordion.Control>
                  Previous attempt: Not yet competent
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap={4}>
                    {previousAttemptAt && (
                      <Text size="sm" c="dimmed">
                        {parseUtcDate(previousAttemptAt).toLocaleDateString()}
                        {previousAttemptByName
                          ? ` · ${previousAttemptByName}`
                          : ''}
                      </Text>
                    )}
                    {previousAttemptComment && (
                      <Text size="sm">{previousAttemptComment}</Text>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}

          <Stack gap="sm" mb={{base: 'lg', lg: 120}}>
            {criteria.map(criterion => (
              <Card key={criterion.id} withBorder shadow="xs" p="md">
                <Text fw={500} mb="sm">
                  {criterion.text}
                </Text>
                <Group gap="xs" mb="xs">
                  {RESULT_OPTIONS.map(option => (
                    <Button
                      key={option.value}
                      color={option.color}
                      variant={
                        results[criterion.id]?.result === option.value
                          ? 'filled'
                          : 'outline'
                      }
                      size="lg"
                      miw={90}
                      mih={44}
                      onClick={() => handleMark(criterion.id, option.value)}>
                      {option.label}
                    </Button>
                  ))}
                </Group>
                {criterion.evidenceType ===
                  SkillCriterionEvidenceTypeEnum.Note && (
                  <Textarea
                    placeholder="Evidence note"
                    value={results[criterion.id]?.note ?? ''}
                    onChange={event =>
                      handleNote(criterion.id, event.currentTarget.value)
                    }
                  />
                )}
              </Card>
            ))}

            {isAllNotApplicable && (
              <Textarea
                label="Comment"
                description="Required when every criterion is marked N/A. Explain why nothing was demonstrable."
                required
                placeholder="Why couldn't this be demonstrated?"
                value={signOffComment}
                onChange={event => setSignOffComment(event.currentTarget.value)}
                autosize
                minRows={2}
              />
            )}
          </Stack>

          <Box
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--mantine-color-body)',
              paddingTop: 'var(--mantine-spacing-sm)',
              paddingBottom: 'var(--mantine-spacing-sm)',
            }}>
            <Group justify="flex-end" wrap="wrap">
              <Button
                size="lg"
                mih={44}
                variant="outline"
                color="red"
                onClick={() => {
                  if (
                    notYetComment.trim() === '' &&
                    signOffComment.trim() !== ''
                  ) {
                    setNotYetComment(signOffComment);
                  }
                  notYetHandlers.open();
                }}>
                Not yet competent
              </Button>
              <Button
                size="lg"
                mih={44}
                disabled={!canSignOff || isSubmitting}
                loading={isSubmitting}
                onClick={handleSignOff}>
                Sign off
              </Button>
            </Group>
            {!allMarked && (
              <Text size="xs" c="dimmed" ta="right" mt={4}>
                Mark every criterion before submitting.
              </Text>
            )}
            {allMarked && hasFail && (
              <Text size="xs" c="red" ta="right" mt={4}>
                A failed criterion cannot be signed off. Use Not yet competent.
              </Text>
            )}
            {allMarked && !hasFail && needsAllNaComment && (
              <Text size="xs" c="red" ta="right" mt={4}>
                Add a comment explaining the all-N/A result before signing off.
              </Text>
            )}
          </Box>

          <Modal
            opened={notYetOpened}
            onClose={notYetHandlers.close}
            title="Not yet competent"
            centered>
            <Stack gap="md">
              <Text size="sm">
                This records the result and creates a new sign-off request so
                the employee can be evaluated again. They will not repeat the
                course.
              </Text>
              <Textarea
                label="Comment"
                description={
                  hasAnyCriterionNote
                    ? 'Optional. Criterion notes were already recorded.'
                    : undefined
                }
                required={needsNotYetComment}
                placeholder="What still needs work?"
                value={notYetComment}
                onChange={event => setNotYetComment(event.currentTarget.value)}
                autosize
                minRows={3}
              />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={notYetHandlers.close}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  disabled={
                    !canSubmitNotYetCompetent ||
                    needsNotYetComment ||
                    isSubmitting
                  }
                  loading={isSubmitting}
                  onClick={handleNotYetCompetent}>
                  Confirm
                </Button>
              </Group>
            </Stack>
          </Modal>

          <ConfirmDialog
            opened={isBlocked}
            onClose={cancelLeave}
            onConfirm={confirmLeave}
            title="Leave without saving?"
            body="You've marked results that haven't been submitted. Leaving now discards them."
            confirmationButtonColor="red"
            buttonTextYes="Leave"
            buttonTextNo="Stay"
          />
        </>
      )}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.TrainingSignOffEvaluate(':signOffId'),
  element: <SignOffEvaluatePage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.Update,
  isPrivate: true,
});
